import { notification } from 'antd';
import { PureComponent, ReactNode } from 'react';
import { pushMCertTemplate } from '../../api/mcertTemplate';
import { captureErrorString, captureError } from '../../pages/captureError';
import {
    CERTIFICATES_CONFIG,
    EKA_VARIABLE_CLASSLIST,
    PREDEFINED_CERTIFICATES_IDS,
} from './medicalCertificates';
import { getMySingleDoctorId } from '../../store/idbHandler';

type Props = {
    heading: string;
    content: string;
    certId?: string;
    type: 'mcert' | 'consent';
};

type State = {
    input: string;
    apiState: 'idle' | 'loading' | 'success' | 'error';
};

class SaveAsTemplateCr extends PureComponent<Props, State> {
    TEMP = 'template-util';
    isUpdate = !PREDEFINED_CERTIFICATES_IDS.includes(this.props.certId || '');
    state: State = {
        input: CERTIFICATES_CONFIG[this.props.certId || '']?.name || '',
        apiState: 'idle',
    };

    onInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({ input: e.target.value });
    };

    replaceVariables = (el: HTMLElement): void => {
        [...el.children].forEach((t) => {
            t && this.replaceVariables(t as HTMLElement);
        });
        if (
            (el.classList.contains(EKA_VARIABLE_CLASSLIST) || el.classList.contains('docSign')) &&
            !el.classList.contains('date')
        ) {
            // console.log(el.classList);
            el.innerText = `{$${el.classList[1] || el.classList[0]}}`;
        }
    };

    saveTemplate = (): void => {
        const utilEl: HTMLElement | null = document.getElementById(this.TEMP);
        if (!utilEl) {
            // TODO handle error
            return;
        }
        const { content } = this.props;
        utilEl.innerHTML = content;
        console.log('before', utilEl.innerHTML);
        this.replaceVariables(utilEl);
        console.log('after', utilEl.innerHTML);

        const { input } = this.state;

        if (!input.trim()) {
            notification.error({
                message: `Please enter a valid template name`,
                duration: 5,
            });
            return;
        }

        this.setState({ apiState: 'loading' });
        pushMCertTemplate(getMySingleDoctorId() || '', {
            heading: encodeURI(this.props.heading),
            template: encodeURI(utilEl.innerHTML),
            name: input.trim(),
            id: this.isUpdate ? this.props.certId : undefined,
            type: this.props.type,
        })
            .then((res) => {
                if (res.success) {
                    notification.success({
                        message: `Template ${this.isUpdate ? 'updated' : 'saved'} successfully`,
                        duration: 5,
                    });
                    this.setState({ apiState: 'success' });
                } else {
                    this.setState({ apiState: 'error' });
                    console.log(res);
                    notification.error({
                        message: `Error ${this.isUpdate ? 'updating' : 'saving'} Template`,
                        duration: 5,
                    });
                    captureErrorString(
                        `Error ${this.isUpdate ? 'updating' : 'saving'} Template` + res,
                    );
                }
            })
            .catch((err) => {
                this.setState({ apiState: 'error' });
                console.error(err);
                notification.error({
                    message: `Error ${this.isUpdate ? 'updating' : 'saving'} Template`,
                    duration: 5,
                });
                captureError(err);
            });
    };

    render(): ReactNode {
        const { input, apiState } = this.state;
        return (
            <div className="flex-1 bg-darwin-neutral-50 px-12 py-8 space-y-8">
                <div className="hidden" id={this.TEMP}></div>
                <p className="font-700">
                    {' '}
                    {this.isUpdate ? 'Update template' : 'Save as a template'}{' '}
                </p>
                <div className="flex items-center border border-darwin-neutral-100 bg-white p-14 rounded-4 space-x-8">
                    <input
                        type="text"
                        placeholder="Type template name..."
                        className="flex-1"
                        value={input}
                        onChange={this.onInputChange}
                    />
                    {apiState === 'idle' ? (
                        <button
                            onClick={this.saveTemplate}
                            className="text-darwin-primary-500 font-700"
                        >
                            {this.isUpdate ? 'Update' : 'Save'}
                        </button>
                    ) : apiState === 'success' ? (
                        <svg
                            className="w-18 h-18 min-w-18 mt-3"
                            viewBox="0 0 16 16"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M8 0C3.5625 0 0 3.59375 0 8C0 12.4375 3.5625 16 8 16C12.4062 16 16 12.4375 16 8C16 3.59375 12.4062 0 8 0ZM11.5938 6.625L7.59375 10.625C7.4375 10.8125 7.21875 10.875 7 10.875C6.75 10.875 6.53125 10.8125 6.375 10.625L4.375 8.625C4.03125 8.28125 4.03125 7.75 4.375 7.40625C4.71875 7.0625 5.25 7.0625 5.59375 7.40625L7 8.78125L10.375 5.40625C10.7188 5.0625 11.25 5.0625 11.5938 5.40625C11.9375 5.75 11.9375 6.28125 11.5938 6.625Z"
                                fill="#1F832D"
                            ></path>
                        </svg>
                    ) : (
                        <svg
                            viewBox="0 0 512 512"
                            className="fill-actionPrimary animate-spin"
                            width="20"
                            height="20"
                        >
                            <path d="M456.433 371.72l-27.79-16.045c-7.192-4.152-10.052-13.136-6.487-20.636 25.82-54.328 23.566-118.602-6.768-171.03-30.265-52.529-84.802-86.621-144.76-91.424C262.35 71.922 256 64.953 256 56.649V24.56c0-9.31 7.916-16.609 17.204-15.96 81.795 5.717 156.412 51.902 197.611 123.408 41.301 71.385 43.99 159.096 8.042 232.792-4.082 8.369-14.361 11.575-22.424 6.92z" />
                        </svg>
                    )}
                </div>
            </div>
        );
    }
}

export default SaveAsTemplateCr;
